/**
 * syncService.js — Background bidirectional sync for desktop (local-first).
 *
 * Every SYNC_INTERVAL ms when online:
 *   1. Push: orders with sync_status='local' → Supabase
 *   2. Pull: recent Supabase orders → local SQLite
 *
 * SQLite is always the source of truth for the UI.
 * Supabase is the cloud sync layer (multi-device, Android, QR).
 */

import { supabase, posService as remotePos } from './supabase'

const SYNC_INTERVAL = 15_000  // 15 seconds

let _timer        = null
let _restaurantId = null
let _syncing      = false
let _isOnline     = false  // starts false; set true after first successful sync
let _onOrdersUpdated = null  // callback: () => void — called after orders are seeded from Supabase

export function isOnline() { return _isOnline }

export async function hasPendingSync(restaurantId) {
  if (!window.menuronDesktop?.isDesktop) return false
  try {
    const [orders, menu, billing, customers] = await Promise.all([
      window.menuronDesktop.local.getPendingOrders(restaurantId),
      window.menuronDesktop.local.getPendingMenu(restaurantId),
      window.menuronDesktop.local.getPendingBillingSettings(restaurantId),
      window.menuronDesktop.local.getPendingCustomers(restaurantId),
    ])
    return (
      (orders?.data?.length > 0) ||
      (menu?.data?.categories?.length > 0 || menu?.data?.menuItems?.length > 0) ||
      !!billing?.data ||
      (customers?.data?.length > 0)
    )
  } catch { return false }
}

/** Register a callback that fires whenever syncCycle or pullNow seeds new orders into SQLite.
 *  POSDashboard uses this to refresh the orders list without relying solely on realtime events. */
export function onOrdersUpdated(cb) { _onOrdersUpdated = cb }

async function syncCycle() {
  if (_syncing || !_restaurantId) return
  _syncing = true

  try {
    // ── 1. Push local orders → Supabase ────────────────────────────────────
    const pending = await window.menuronDesktop.local.getPendingOrders(_restaurantId)
    if (pending?.data?.length > 0) {
      const syncedIds = []

      for (const order of pending.data) {
        // Strip SQLite-only fields before upserting to Supabase
        let {
          order_items, restaurant_tables,
          sync_status, local_version, synced_at, deleted_locally,
          terminal_id,
          ...orderRow
        } = order

        // Coerce SQLite integer booleans to JS booleans for Supabase boolean columns
        orderRow = { ...orderRow, is_invalid: Boolean(orderRow.is_invalid) }

        let { error: orderErr } = await supabase
          .from('orders')
          .upsert(orderRow, { onConflict: 'id' })

        if (orderErr?.code === '23505' && orderErr.message?.includes('order_number')) {
          // Order number conflict with a QR/other device order — use timestamp fallback
          const fallbackNum = String(Date.now()).slice(-6)
          orderRow = { ...orderRow, order_number: fallbackNum }
          const retry = await supabase.from('orders').upsert(orderRow, { onConflict: 'id' })
          orderErr = retry.error
          if (!retry.error) {
            // Update local SQLite so the order number stays consistent
            await window.menuronDesktop.local.updateOrderNumber(order.id, fallbackNum)
            console.warn(`[sync] order_number conflict resolved — reassigned to ${fallbackNum}`)
          }
        }

        if (orderErr?.code === '23503' && orderErr.message?.includes('customer_id')) {
          // Customer not yet synced to Supabase — retry without customer_id (name/phone are plain text, kept)
          const { customer_id, ...orderWithoutCustomer } = orderRow
          const retry = await supabase.from('orders').upsert(orderWithoutCustomer, { onConflict: 'id' })
          orderErr = retry.error
          if (!retry.error) {
            console.warn(`[sync] customer_id FK violation — synced order without customer link`)
          }
        }

        if (orderErr) {
          console.warn('[sync] order upsert failed:', orderErr.message)
          continue  // don't mark this order as synced — retry next cycle
        }

        if (order_items?.length > 0) {
          const items = order_items.map(item => ({
            id:           item.id,
            order_id:     order.id,
            menu_item_id: item.menu_items?.id || null,
            quantity:     item.quantity,
            unit_price:   item.unit_price,
            total_price:  item.total_price,
          }))
          await supabase.from('order_items').upsert(items, { onConflict: 'id' })
        }

        syncedIds.push(order.id)
      }

      if (syncedIds.length > 0) {
        await window.menuronDesktop.local.markOrdersSynced(syncedIds)
        console.log(`[sync] pushed ${syncedIds.length} orders to Supabase`)
      }
    }

    // ── 2. Push local menu changes → Supabase ───────────────────────────────
    const localMenu = await window.menuronDesktop.local.getPendingMenu(_restaurantId)
    if (localMenu?.data) {
      const { categories = [], menuItems = [] } = localMenu.data
      for (const cat of categories) {
        const { sync_status, local_version, deleted_locally, synced_at, ...catRow } = cat
        if (deleted_locally) {
          await supabase.from('categories').update({ deleted: true }).eq('id', cat.id)
        } else {
          await supabase.from('categories').upsert(catRow, { onConflict: 'id' })
        }
      }
      for (const item of menuItems) {
        const { sync_status, local_version, deleted_locally, synced_at, ...itemRow } = item
        if (deleted_locally) {
          await supabase.from('menu_items').delete().eq('id', item.id)
        } else {
          await supabase.from('menu_items').upsert(itemRow, { onConflict: 'id' })
        }
      }
      if (categories.length + menuItems.length > 0) {
        await window.menuronDesktop.local.markMenuSynced(_restaurantId)
        console.log(`[sync] pushed ${categories.length} categories, ${menuItems.length} menu items`)
      }
    }

    // ── 3. Push billing settings → Supabase ─────────────────────────────────
    const pendingBilling = await window.menuronDesktop.local.getPendingBillingSettings(_restaurantId)
    if (pendingBilling?.data) {
      const { sync_status, local_version, synced_at, daily_invoice_seq, daily_invoice_date, ...billingRow } = pendingBilling.data
      const { error: billingErr } = await supabase
        .from('restaurant_billing_settings')
        .upsert(billingRow, { onConflict: 'restaurant_id' })
      if (!billingErr) {
        await window.menuronDesktop.local.markBillingSettingsSynced(_restaurantId)
        console.log('[sync] pushed billing settings to Supabase')
      } else {
        console.warn('[sync] billing settings upsert failed:', billingErr.message)
      }
    }

    // ── 4. Push pending customers → Supabase ────────────────────────────────
    const pendingCustomers = await window.menuronDesktop.local.getPendingCustomers(_restaurantId)
    if (pendingCustomers?.data?.length > 0) {
      const syncedCustomerIds = []
      for (const c of pendingCustomers.data) {
        const { sync_status, local_version, synced_at, deleted_locally, ...customerRow } = c
        if (deleted_locally) {
          const { error } = await supabase.from('customers').delete().eq('id', c.id)
          if (!error) syncedCustomerIds.push(c.id)
          else console.warn('[sync] customer delete failed:', error.message)
        } else {
          const { error } = await supabase.from('customers').upsert(customerRow, { onConflict: 'id' })
          if (!error) syncedCustomerIds.push(c.id)
          else console.warn('[sync] customer upsert failed:', error.message)
        }
      }
      if (syncedCustomerIds.length > 0) {
        await window.menuronDesktop.local.markCustomersSynced(syncedCustomerIds)
        console.log(`[sync] pushed ${syncedCustomerIds.length} customers to Supabase`)
      }
    }

    // ── 5. Pull recent Supabase orders → local SQLite ───────────────────────
    const res = await remotePos.getCounterOrders(_restaurantId, { days: 2 })
    if (!res.error && Array.isArray(res.data)) {
      await window.menuronDesktop.local.seedData({ orders: res.data })
      if (_onOrdersUpdated) _onOrdersUpdated()  // notify UI to refresh
      console.log(`[sync] pulled ${res.data.length} orders from Supabase`)
    }

    // ── 6. Pull tables from Supabase → local SQLite ──────────────────────────
    if (window.menuronDesktop?.isDesktop) {
      const { data: tables } = await supabase
        .from('restaurant_tables')
        .select('*')
        .eq('restaurant_id', _restaurantId)
        .eq('is_active', true)
      if (tables?.length > 0) {
        await window.menuronDesktop.local.seedData({ tables })
        console.log(`[sync] pulled ${tables.length} tables from Supabase`)
      }
    }

    _isOnline = true

  } catch (e) {
    // Network unavailable — silent, will retry next cycle
    _isOnline = false
    console.log('[sync] cycle skipped:', e.message)
  } finally {
    _syncing = false
  }
}

// Seed a single order from realtime payload.new directly into SQLite — no Supabase fetch.
// Call this on postgres_changes INSERT so the card appears instantly before items arrive.
// No-op in browser mode.
export async function seedOrderNow(order) {
  if (!order?.id || !window.menuronDesktop?.isDesktop) return
  try {
    await window.menuronDesktop.local.seedData({ orders: [order] })
    if (_onOrdersUpdated) _onOrdersUpdated()  // notify UI to refresh
    console.log('[seedOrderNow] seeded order', order.id, 'into SQLite')
  } catch (e) {
    console.log('[seedOrderNow] exception:', e.message)
  }
}

// Pull-only: call this immediately when a realtime event fires (new QR/waiter order)
// so the new order is in SQLite before the component reads it.
// No-op in browser mode or when sync hasn't started.
export async function pullNow() {
  if (!_restaurantId || !window.menuronDesktop?.isDesktop) {
    console.log('[pullNow] skipped — restaurantId:', _restaurantId, 'isDesktop:', window.menuronDesktop?.isDesktop)
    return
  }
  try {
    // Small delay so order_items (inserted after the order row) are visible on Supabase's read path.
    // broadcast new_order fires after both order + items are committed, so 200ms is enough.
    await new Promise(r => setTimeout(r, 200))
    console.log('[pullNow] fetching from Supabase for restaurant', _restaurantId)
    const res = await remotePos.getCounterOrders(_restaurantId, { days: 2 })
    console.log('[pullNow] got', res.data?.length, 'orders, error:', res.error?.message)
    if (!res.error && Array.isArray(res.data)) {
      await window.menuronDesktop.local.seedData({ orders: res.data })
      if (_onOrdersUpdated) _onOrdersUpdated()  // notify UI to refresh
      console.log('[pullNow] seeded', res.data.length, 'orders into SQLite')
    }
  } catch (e) {
    console.log('[pullNow] exception:', e.message)
  }
}

export function startSync(restaurantId) {
  stopSync()                    // clear old timer first (resets _restaurantId to null)
  _restaurantId = restaurantId  // then set the new id
  syncCycle()                   // immediate first run to catch up
  _timer = setInterval(syncCycle, SYNC_INTERVAL)
  window.addEventListener('online', syncCycle)
  console.log('[sync] started for restaurant', restaurantId)
}

export function stopSync() {
  if (_timer) { clearInterval(_timer); _timer = null }
  window.removeEventListener('online', syncCycle)
  _restaurantId = null
}
